import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  Output,
  signal,
  ViewChildren,
  QueryList
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, take, tap } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import {
  AlertComponent,
  ButtonComponent,
  CardComponent,
  FormValueChangedEvent,
  InlineFormSchemaComponent,
  LoaderModule,
  ObjectCardComponent,
  StepIndicatorComponent,
  StepIndicatorItem
} from '@mds/ui';
import { MdsIcon, MdsIconsModule } from '@mds/icons';
import { ComposerApiService } from '@services/composer-api.service';
import { ActionItem, ModalActionEnum } from '@dialogs/actions-panel/actions-panel.component';

type OperationInputs = {
  op_source_pg?: string;
  op_target_pg?: string;
  op_target_process?: string;
};

interface FormSchemaItem {
  key: string;
  selected_tile?: TileCard;
  [prop: string]: unknown;
}

interface SectionLine {
  value?: string;
  [prop: string]: unknown;
}

interface OperationSection {
  name: string;
  formSchema: FormSchemaItem[];
  lines: SectionLine[];
}

interface OperationData {
  targetRid: string;
  sections: OperationSection[];
  actionWarning?: {
    missingOperationKeys?: string[];
  };
}

interface TileCard {
  rid?: string;
  value?: string;
  [prop: string]: unknown;
}

@Component({
  selector: 'app-operation',
  standalone: true,
  imports: [
    AlertComponent,
    ButtonComponent,
    CommonModule,
    CardComponent,
    LoaderModule,
    MdsIconsModule,
    InlineFormSchemaComponent,
    ObjectCardComponent,
    StepIndicatorComponent
  ],
  templateUrl: './operation.component.html',
  styleUrls: ['./operation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OperationComponent {
  private _data: OperationData | undefined;

  @Input()
  set data(value: OperationData | undefined) {
    if (value === this._data) return;
    this._data = value;
    if (value) {
      this.onDataChanged(value);
    }
  }

  get data(): OperationData | undefined {
    return this._data;
  }

  @Output() reloadData = new EventEmitter<void>();
  @Output() actionToPerform = new EventEmitter<ActionItem[]>();
  @ViewChildren(InlineFormSchemaComponent) private inlineForms: QueryList<InlineFormSchemaComponent>;

  sharedModel: { Inputs: OperationInputs } = { Inputs: {} };
  cardDataMap: Record<string, TileCard> = {};
  sectionsReady = false;
  targetProcessVersion = 0;
  currentStep = 1;
  protected readonly missingOperationKeys = new Set<string>();
  private targetProductRid?: string;
  private currentSections: OperationSection[] = [];
  private suppressFormEvent = false;
  protected readonly MdsIcon = MdsIcon;
  private readonly fetchCardData$ = new Subject<void>();

  private readonly composerApiService = inject(ComposerApiService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  readonly steps: StepIndicatorItem[] = [
    { stepNumber: 1, label: 'Select component' },
    { stepNumber: 2, label: 'Summary' }
  ];

  get sourceSection(): OperationSection | undefined {
    return this.currentSections.find((s) => s.formSchema?.[0]?.key === 'op_source_pg');
  }

  get targetSection(): OperationSection | undefined {
    return this.currentSections.find((s) => s.formSchema?.[0]?.key === 'op_target_pg');
  }

  get processSection(): OperationSection | undefined {
    return this.currentSections.find((s) => s.formSchema?.[0]?.key === 'op_target_process');
  }

  get isStep1Complete(): boolean {
    const forms = this.inlineForms?.toArray();
    if (!forms?.length) return false;
    return forms.every((f) => f.form.valid);
  }

  get stepTitle(): string {
    return this.currentStep === 1 ? 'Select components' : 'Summary';
  }

  get stepSubtitle(): string {
    return this.currentStep === 1
      ? 'Choose what components you want this action to replace'
      : '';
  }

  constructor() {
    this.fetchCardData$
      .pipe(
        debounceTime(300),
        map(() => {
          const ridEntries: { key: string; rid: string }[] = [];
          const currentInputs = this.sharedModel.Inputs;
          this.currentSections.forEach((section) => {
            const key = section.formSchema?.[0]?.key;
            const rid = currentInputs[key as keyof OperationInputs];
            if (key && rid) {
              ridEntries.push({ key, rid });
            }
          });
          return ridEntries;
        }),
        distinctUntilChanged((prev, curr) => {
          if (prev.length !== curr.length) return false;
          return prev.every((p, i) => p.key === curr[i].key && p.rid === curr[i].rid);
        }),
        switchMap((ridEntries) => {
          if (!ridEntries.length) {
            this.sectionsReady = true;
            this.changeDetectorRef.markForCheck();
            return EMPTY;
          }

          const uniqueRids = [...new Set(ridEntries.map((e) => e.rid))];
          return this.composerApiService.getTileCards(uniqueRids).pipe(
            tap((cards: TileCard[]) => {
              const lookup = new Map(cards.map((card) => [card.rid || card.value, card]));
              const newMap: Record<string, TileCard> = {};
              ridEntries.forEach(({ key, rid }) => {
                const card = lookup.get(rid);
                if (card) {
                  newMap[key] = card;
                  this.injectSelectedTile(key, card);
                }
              });
              this.cardDataMap = newMap;
              this.sectionsReady = true;
              this.changeDetectorRef.markForCheck();
            }),
            catchError(() => {
              this.sectionsReady = true;
              this.changeDetectorRef.markForCheck();
              return EMPTY;
            })
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  hasMissingOperation(section: OperationSection): boolean {
    return this.missingOperationKeys.has(section.formSchema?.[0]?.key);
  }

  formValueChanged(_sectionName: string, event: FormValueChangedEvent): void {
    if (this.suppressFormEvent) return;

    const formInputs = (event.value?.Inputs ?? {}) as OperationInputs;
    const prevInputs = this.sharedModel.Inputs;
    const merged: OperationInputs = { ...prevInputs, ...formInputs };

    const changed = (Object.keys(merged) as (keyof OperationInputs)[]).some(
      (k) => merged[k] !== prevInputs[k]
    );
    if (!changed) {
      this.emitStepActions();
      return;
    }

    this.sharedModel.Inputs = merged;

    const targetProductRid = merged.op_target_pg;
    if (targetProductRid !== this.targetProductRid) {
      this.targetProductRid = targetProductRid;
      this.clearProcessSection();
      this.rebuildSharedModel({ ...merged, op_target_process: undefined });
      this.targetProcessVersion++;
    }
    this.changeDetectorRef.markForCheck();
    this.fetchCardData$.next();
    this.emitStepActions();
  }

  chooseSameAsOriginal(): void {
    const sourceRid = this.sharedModel.Inputs.op_source_pg;
    if (!sourceRid) return;

    this.targetProductRid = sourceRid;
    this.clearProcessSection();

    this.composerApiService.getTileCards([sourceRid]).pipe(take(1)).subscribe((cards) => {
      const card = cards[0];
      if (card) {
        this.cardDataMap = { ...this.cardDataMap, op_target_pg: card };
        this.injectSelectedTile('op_source_pg', card);
        this.injectSelectedTile('op_target_pg', card);
      }
      this.rebuildSharedModel({
        ...this.sharedModel.Inputs,
        op_target_pg: sourceRid,
        op_target_process: undefined
      });
      this.targetProcessVersion++;
      this.fetchCardData$.next();
      this.emitStepActions();
    });
  }

  goToStep(step: number): void {
    this.currentStep = step;
    this.emitStepActions();
    this.changeDetectorRef.markForCheck();
  }

  private onDataChanged(currentData: OperationData): void {
    this.currentSections = currentData.sections ?? [];
    this.targetProcessVersion = 0;
    this.missingOperationKeys.clear();
    currentData.actionWarning?.missingOperationKeys?.forEach((key) =>
      this.missingOperationKeys.add(key)
    );
    this.targetProductRid = this.currentSections
      .find((section) => section.formSchema?.[0]?.key === 'op_target_pg')
      ?.lines?.[0]?.value;

    const inputs: OperationInputs = {};
    this.currentSections.forEach((section) => {
      const key = section.formSchema?.[0]?.key as keyof OperationInputs;
      const value = section.lines?.[0]?.value;
      if (key && value) {
        inputs[key] = value;
      }
    });
    this.rebuildSharedModel(inputs);

    this.cardDataMap = {};
    this.sectionsReady = false;
    this.fetchCardData$.next();
    this.determineInitialStep();
    this.emitStepActions();
    this.changeDetectorRef.markForCheck();
  }

  private clearProcessSection(): void {
    const processSection = this.currentSections.find(
      (s) => s.formSchema?.[0]?.key === 'op_target_process'
    );
    if (processSection?.lines?.[0]) {
      processSection.lines[0].value = undefined;
    }
    if (processSection?.formSchema?.[0]) {
      processSection.formSchema[0].selected_tile = undefined;
    }
    delete this.cardDataMap['op_target_process'];
  }

  private rebuildSharedModel(inputs: OperationInputs): void {
    this.suppressFormEvent = true;
    this.sharedModel = { Inputs: inputs };
    this.changeDetectorRef.markForCheck();
    queueMicrotask(() => (this.suppressFormEvent = false));
  }

  private injectSelectedTile(key: string, card: TileCard): void {
    const section = this.currentSections.find(
      (s) => s.formSchema?.[0]?.key === key
    );
    if (!section?.formSchema?.[0]) return;
    section.formSchema[0].selected_tile = card;
  }

  private determineInitialStep(): void {
    const hasExistingData = this.currentSections.some(
      (section) => section.lines?.[0]?.value
    );
    this.currentStep = hasExistingData ? 2 : 1;
  }

  private emitStepActions(): void {
    if (this.currentStep === 1) {
      if (this.isStep1Complete) {
        this.actionToPerform.emit([
          {
            label: 'Finish action',
            icon: MdsIcon.CHECKED,
            value: ModalActionEnum.SAVE,
            callback: () => this.save()
          }
        ]);
      } else {
        this.actionToPerform.emit([]);
      }
    } else {
      this.actionToPerform.emit([
        {
          label: 'Edit operation',
          icon: MdsIcon.EDIT,
          value: ModalActionEnum.EDIT,
          callback: () => this.goToStep(1)
        }
      ]);
    }
  }

  private save(): void {
    const forms = this.inlineForms.toArray();
    if (!forms.length || forms.some((form) => form.form.invalid)) {
      forms.forEach((form) => form.validateForm());
      return;
    }
    const payload = Object.fromEntries(
      Object.entries(this.sharedModel.Inputs).filter(
        ([key, value]) => key.startsWith('op_') && value
      )
    );
    if (!this._data?.targetRid) return;
    this.composerApiService.submitEditedMetadata(this._data.targetRid, payload).subscribe({
      next: () => this.reloadData.emit(),
      error: () => {
        forms.forEach((form) => form.validateForm());
      }
    });
  }
}
